// Builds the standalone HTML report for ONE SKU's forecast run — the SKU
// counterpart of forecastArtifactHtml.js (which reports on a whole product).
// Everything comes from what's already stored for that SKU, so it agrees
// with the SKU tab on screen:
//  - `run`:        the SKU's forecast-data record — forecast + history,
//                  forecast_model (method, tested error, warnings), metrics
//                  (stock, reorder), ledger, allocation, and the SKU insight
//                  / explanation HTML the insight workflow wrote
//  - `productSku`: this SKU's row from the product artifact's narrative
//                  (status + recommendation), when that artifact is ready
// Self-contained page (Chart.js from a CDN), for an iframe srcDoc / download.

import { plainReasons, DEMAND_TYPE_LABEL } from "../planning-forecast/ForecastModelSection";
import {
  ledgerDayLabel,
  ledgerMonthLabel,
  ledgerStockNotes,
  poArrivalsOf,
  reorderDateNote,
} from "../../utils/inventory-ledger-xlsx-export";
import { isFilledRow, salesFillPoints } from "../../utils/sales-fill";

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const isNum = (v) => v != null && v !== "" && Number.isFinite(Number(v));
const n0 = (v) => (isNum(v) ? Math.round(Number(v)).toLocaleString("en-US") : "—");
const n1 = (v) => (isNum(v) ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 }) : "—");
const signed = (v) => (isNum(v) && Math.round(Number(v)) !== 0 ? `${Number(v) > 0 ? "+" : "−"}${n0(Math.abs(Number(v)))}` : n0(v));
const fmtDate = (d) =>
  d && !Number.isNaN(new Date(d).getTime())
    ? new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
    : "—";

const safeJson = (obj) => JSON.stringify(obj).replace(/</g, "\\u003c");

// The insight / explanation are the workflow's own HTML; keep its markup but
// drop anything executable before it goes into a page that runs scripts.
export const cleanHtml = (html) =>
  String(html || "")
    .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, "$1=$2#$2");

const BUCKET = { monthly: "month", weekly: "week", daily: "day" };
const periodLabel = (date, type) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return String(date);
  return String(type || "").toLowerCase() === "monthly"
    ? d.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" })
    : d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "2-digit", timeZone: "UTC" });
};
const historyDate = (r) => r?.date ?? r?.ds ?? r?.period ?? r?.month;
const historyQty = (r) => Number(r?.quantity ?? r?.actual_qty ?? r?.qty ?? r?.value ?? r?.demand ?? 0) || 0;

const statusClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (/critical|heavily|stock ?out/.test(s)) return "badge-red";
  if (/low|over/.test(s)) return "badge-yellow";
  if (/healthy|good|balanced/.test(s)) return "badge-green";
  return "badge-blue";
};

const table = (head, rows) =>
  `<div class="scroll-table"><table><thead><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${rows
    .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`)
    .join("")}</tbody></table></div>`;

/** The inventory ledger as an HTML table: months across, figures down — as on the page. */
const ledgerHtml = (ledger) => {
  const months = ledger?.months || [];
  if (!months.length) return "";
  const arrivals = poArrivalsOf(ledger);
  const nextArrival = arrivals.size ? arrivals.get([...arrivals.keys()].sort()[0]) : null;
  const notes = ledgerStockNotes(ledger);
  const out = (m, text, { moves = false } = {}) => {
    const items = [...notes.cellItems(m), ...(moves ? notes.moveItems(m) : [])];
    const note = notes.cellItems(m).length > 0;
    const move = moves && notes.moveItems(m).length > 0;
    // A browser tooltip: one point per line.
    const title = items.map((t) => `• ${t}`).join("\n");
    if (!title) return text;
    return `<span class="${note ? "bad" : "warn"}" title="${esc(title)}">${text}${move ? " <small class=\"warn\">!</small>" : ""}</span>`;
  };
  const warehouses = (ledger.warehouses || []).filter((w) => w.pct > 0);
  const shareSum = warehouses.reduce((s, w) => s + w.pct, 0) || 1;
  const line = (label, fn, cls = "") =>
    `<tr class="${cls}"><th class="row-head">${label}</th>${months
      .map((m) => `<td class="${m.kind === "current" ? "now" : ""}">${fn(m)}</td>`)
      .join("")}</tr>`;
  const rows = [
    line("On hand (month end)", (m) =>
      m.kind !== "future" && m.stockout?.days
        ? `${out(m, n0(m.on_hand), { moves: true })} <small class="bad">${m.stockout.days}d out</small>`
        : out(m, n0(m.on_hand), { moves: true }),
    ),
    line("POs arrived / arriving", (m) => n0(m.kind === "past" ? m.po_received : m.po_inbound)),
    line("Transfers in / out", (m) => signed(m.kind === "past" ? m.transfer_received : m.transfer_net)),
    line("Sales", (m) => out(m, n0(m.sales))),
    ...(months.some((m) => m.shortfall > 0) ? [line("Shortfall", (m) => (m.shortfall > 0 ? `<span class="bad">${n0(m.shortfall)}</span>` : "—"))] : []),
    line("Forecast", (m) => n0(m.forecast)),
    line(
      `PO arrival (recommended)${
        nextArrival
          ? `<div class="po-sub${nextArrival.overdue ? " bad" : ""}" title="${esc(reorderDateNote(nextArrival))}">Reorder Date: ${esc(ledgerDayLabel(nextArrival.reorder_date))}</div>`
          : ""
      }<div class="po-sub">${n0(ledger.lead_time_days)} days of lead time</div>`,
      (m) => {
        if (m.kind === "past") return "—";
        const a = arrivals.get(m.month);
        return a ? `<span class="good">${n0(a.qty)}</span>` : "0";
      },
      "strong",
    ),
    ...(arrivals.size
      ? warehouses.map((w) =>
          line(`&nbsp;&nbsp;${esc(w.name || "Warehouse")} (${Math.round((w.pct / shareSum) * 100)}%)`, (m) =>
            arrivals.has(m.month) ? n0(arrivals.get(m.month).by_warehouse[w.id] ?? 0) : "—",
          ),
        )
      : []),

  ];
  return `<div class="chart-card"><h3>Inventory Ledger</h3>
<p class="muted note">On hand from the daily inventory snapshot${ledger.stock_as_of ? ` (latest ${esc(fmtDate(ledger.stock_as_of))})` : ""}. PO arrival is the recommended order in the month it arrives; place it ${esc(n0(ledger.lead_time_days))} days earlier. Keeps at least ${esc(n0(ledger.safety_stock))} units of safety stock${ledger.min_order_qty ? `, at least ${esc(n0(ledger.min_order_qty))} per order` : ""}.</p>
${notes.bannerItems.length ? `<div class="insight"><ul class="pts">${notes.bannerItems.map((t) => `<li>${esc(t)}</li>`).join("")}</ul></div>` : ""}
<div class="scroll-table wide"><table class="ledger"><thead><tr><th></th>${months
    .map((m) => `<th class="${m.kind === "current" ? "now" : ""}">${esc(ledgerMonthLabel(m.month))}${m.kind === "current" ? "<br><small>today</small>" : ""}</th>`)
    .join("")}</tr></thead><tbody>${rows.join("")}</tbody></table></div></div>`;
};

export const buildSkuArtifactHtml = ({ run, productSku, darkMode = false }) => {
  const input = run?.input || {};
  const type = input.type || "Monthly";
  const unit = BUCKET[String(type).toLowerCase()] || "period";
  const model = run?.forecast_model || null;
  const metrics = run?.metrics || {};
  const forecast = (run?.forecast_adjusted || []).slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const history = (Array.isArray(run?.historical_data) ? run.historical_data : [])
    .filter((r) => historyDate(r))
    .slice()
    .sort((a, b) => new Date(historyDate(a)) - new Date(historyDate(b)));
  // Enough history to read the trend into the forecast, without crowding it —
  // all of it when part is estimated (sales fill), so the estimate is shown.
  const shownHistory = history.some(isFilledRow) ? history : history.slice(-Math.max(12, forecast.length * 2));
  const fill = salesFillPoints(model?.sales_fill);
  const productName = input.product?.name || "";
  const forecastTotal = forecast.reduce((s, f) => s + (Number(f.predicted_qty) || 0), 0);
  const hasRange = forecast.length > 0 && forecast.every((f) => isNum(f.lower) && isNum(f.upper));
  const upliftTotal = forecast.reduce((s, f) => s + (Number(f.event_uplift) || 0), 0);

  const theme = darkMode
    ? { bg: "#141824", text: "#E2E8F0", textSecondary: "#8892A4", textMuted: "#6B7280", cardBg: "#1B1F2E", border: "#2C3045", shadow: "0 1px 3px rgba(0,0,0,.4)", tableHeadBg: "#242938", nowBg: "#1E2A44", headerGrad1: "#0C0E17", headerGrad2: "#05060A", insightBg: "#2A2410", insightBorder: "#CA9A2E", gridLine: "rgba(255,255,255,.08)", chartText: "#AEB6C4" }
    : { bg: "#f0f2f5", text: "#1a1a2e", textSecondary: "#666", textMuted: "#888", cardBg: "#fff", border: "#e0e0e0", shadow: "0 1px 3px rgba(0,0,0,.08)", tableHeadBg: "#f8f9fa", nowBg: "#EEF3FF", headerGrad1: "#1a1a2e", headerGrad2: "#16213e", insightBg: "#fff8e1", insightBorder: "#ffc107", gridLine: "rgba(0,0,0,.08)", chartText: "#444" };

  const kpi = (title, value, sub) =>
    `<div class="card"><h3>${esc(title)}</h3><div class="value">${esc(value)}</div><div class="sub">${esc(sub)}</div></div>`;

  // ---- chart: history then forecast on one time axis ----------------------
  const labels = [...shownHistory.map((r) => periodLabel(historyDate(r), type)), ...forecast.map((f) => periodLabel(f.date, type))];
  const pad = (arr, before, after) => [...Array(before).fill(null), ...arr, ...Array(after).fill(null)];
  const chartData = {
    labels,
    // Real sales and the sales-fill estimates as separate lines.
    history: pad(shownHistory.map((r) => (isFilledRow(r) ? null : historyQty(r))), 0, forecast.length),
    estimated: shownHistory.some(isFilledRow)
      ? pad(shownHistory.map((r, i, all) => (isFilledRow(r) || (i > 0 && isFilledRow(all[i - 1])) ? historyQty(r) : null)), 0, forecast.length)
      : null,
    forecast: pad(forecast.map((f) => Math.round(Number(f.predicted_qty) || 0)), shownHistory.length, 0),
    low: hasRange ? pad(forecast.map((f) => Math.round(Number(f.lower))), shownHistory.length, 0) : null,
    high: hasRange ? pad(forecast.map((f) => Math.round(Number(f.upper))), shownHistory.length, 0) : null,
    theme: { text: theme.chartText, grid: theme.gridLine },
  };

  // ---- sections ----------------------------------------------------------
  const insight = cleanHtml(run?.insight);
  const explanation = cleanHtml(run?.explanation);
  const reasons = plainReasons(model);

  const forecastRows = forecast.map((f) => [
    esc(periodLabel(f.date, type)),
    n0(f.predicted_qty),
    ...(hasRange ? [`${n0(f.lower)} – ${n0(f.upper)}`] : []),
    ...(upliftTotal ? [Number(f.event_uplift) ? `+${n0(f.event_uplift)}` : "—"] : []),
  ]);

  const reorderRows = [
    ["On hand", n0(metrics.total_on_hand)],
    [metrics.incoming_po_units != null ? "Incoming (open POs + transfers)" : "In transit", n0(metrics.total_in_transit)],
    ["Average daily demand", n1(metrics.avg_daily_demand)],
    ["Lead time", isNum(metrics.lead_time_days) ? `${n0(metrics.lead_time_days)} days` : "—"],
    ["Safety stock", n0(metrics.safety_stock_used)],
    ["Reorder point", n0(metrics.reorder_point)],
    ["Recommended order quantity", `<strong>${n0(metrics.recommended_order_qty)}</strong>`],
    ["Reorder by", esc(fmtDate(metrics.reorder_by_date))],
    ...(metrics.min_order_qty ? [["Minimum order quantity", n0(metrics.min_order_qty)]] : []),
  ].filter(Boolean);

  const allocation = run?.allocation?.warehouses || [];
  const allocationHtml = allocation.length
    ? `<div class="chart-card"><h3>Where to stock it</h3>${table(
        ["Warehouse", "Share", "Avg Zone", "Forecast Units"],
        allocation
          .slice()
          .sort((a, b) => (b.forecast_units || 0) - (a.forecast_units || 0))
          .map((w) => [`<strong>${esc(w.name || "Warehouse")}</strong>`, `${n1((Number(w.pct) || 0) * 100)}%`, w.avg_zone == null ? "—" : n1(w.avg_zone), n0(w.forecast_units)]),
      )}</div>`
    : "";

  const methodHtml = model
    ? `<div class="chart-card"><h3>How this forecast was made</h3>
<p class="lead-line"><strong>${esc(model.model_label || model.model || "")}</strong>${model.demand_type ? ` · ${esc(DEMAND_TYPE_LABEL[model.demand_type] || model.demand_type)}` : ""}${isNum(model.tested_error?.wape) ? ` · tested error ${n1(model.tested_error.wape)}%` : ""}</p>
${reasons.length ? `<dl class="reasons">${reasons.map((r) => `<dt>${esc(r.title)}</dt><dd>${esc(r.text)}</dd>`).join("")}</dl>` : ""}
${(model.warnings || []).length ? `<div class="insight">${model.warnings.map((w) => esc(w)).join("<br>")}</div>` : ""}
${explanation ? `<div class="rich">${explanation}</div>` : ""}</div>`
    : explanation
      ? `<div class="chart-card"><h3>How this forecast was made</h3><div class="rich">${explanation}</div></div>`
      : "";

  const statusHtml = productSku?.status || productSku?.recommendation
    ? `<div class="chart-card"><h3>Status &amp; Recommendation</h3>${productSku.status ? `<p><span class="badge ${statusClass(productSku.status)}">${esc(productSku.status)}</span></p>` : ""}${productSku.recommendation ? `<p style="margin-top:8px;font-size:14px;line-height:1.6">${esc(productSku.recommendation)}</p>` : ""}<p class="muted" style="margin-top:8px">From the product analysis.</p></div>`
    : "";

  const subtitle = [productName, `${forecast.length} ${unit}${forecast.length === 1 ? "" : "s"} forecast`, `run ${fmtDate(run?.createdAt)}`]
    .filter(Boolean)
    .join(" &bull; ");

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(run?.sku)} - SKU Analysis</title>
<script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:${theme.bg};color:${theme.text};padding:24px}
.container{max-width:1400px;margin:0 auto}
.header{background:linear-gradient(135deg,${theme.headerGrad1} 0%,${theme.headerGrad2} 100%);color:#fff;padding:28px 32px;border-radius:16px;margin-bottom:24px}
.header h1{font-size:28px;font-weight:700}.header p{opacity:.8;margin-top:6px;font-size:14px}
.grid{display:flex;flex-wrap:wrap;gap:14px;margin-bottom:24px}
.card{flex:1 1 160px;background:${theme.cardBg};border-radius:12px;padding:16px;box-shadow:${theme.shadow}}
.card h3{font-size:11.5px;text-transform:uppercase;letter-spacing:.5px;color:${theme.textSecondary};margin-bottom:6px}
.card .value{font-size:24px;font-weight:700}.card .sub{font-size:12px;color:${theme.textSecondary};margin-top:4px}
.chart-card{background:${theme.cardBg};border-radius:12px;padding:20px;box-shadow:${theme.shadow};margin-bottom:16px}
.chart-card h3{font-size:16px;font-weight:600;margin-bottom:14px}
.chart-wrap{position:relative;height:320px}
table{width:100%;border-collapse:collapse;font-size:14px;color:${theme.text}}
th{background:${theme.tableHeadBg};text-align:left;padding:9px 12px;font-weight:600;color:${theme.textSecondary};border-bottom:2px solid ${theme.border};white-space:nowrap}
td{padding:9px 12px;border-bottom:1px solid ${theme.border};vertical-align:top}
.ledger td,.ledger th{text-align:right;white-space:nowrap}
.ledger th.row-head{text-align:left;position:sticky;left:0;background:${theme.cardBg};color:${theme.text}}
.ledger .now{background:${theme.nowBg}}
.ledger tr.strong td,.ledger tr.strong th{font-weight:700}
.good{color:#15803d;font-weight:700}.bad{color:#dc2626;font-weight:700}.warn{color:#d97706;font-weight:700}
.po-sub{font-size:10.5px;font-weight:500;color:${theme.textMuted};line-height:1.35}.po-sub.bad{color:#dc2626}
ul.pts{margin:0;padding-left:18px}ul.pts li{margin:2px 0;line-height:1.55}
.scroll-table{overflow:auto;max-height:420px}.scroll-table.wide{max-height:none}
.muted{font-size:12px;color:${theme.textMuted}}.note{margin:-6px 0 12px;font-size:12.5px}
.badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600}
.badge-green{background:#d4edda;color:#155724}.badge-yellow{background:#fff3cd;color:#856404}
.badge-red{background:#f8d7da;color:#721c24}.badge-blue{background:#d1ecf1;color:#0c5460}
.insight{background:${theme.insightBg};border-left:4px solid ${theme.insightBorder};padding:12px 14px;border-radius:8px;margin:12px 0;font-size:13.5px}
.rich{font-size:14px;line-height:1.65}.rich ul,.rich ol{padding-left:20px}.rich p{margin:6px 0}.rich li{margin:3px 0}
.lead-line{font-size:14px;margin-bottom:10px}
dl.reasons dt{font-weight:600;font-size:13.5px;margin-top:10px}dl.reasons dd{font-size:13.5px;line-height:1.6;color:${theme.textSecondary}}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media(max-width:768px){.two-col{grid-template-columns:1fr}body{padding:12px}}
</style></head><body><div class="container">
<div class="header"><h1>${esc(run?.sku)}</h1><p>${subtitle}</p></div>
<div class="grid">
${kpi("On Hand", n0(metrics.total_on_hand), "units across warehouses")}
${kpi("Incoming", n0(metrics.total_in_transit), metrics.incoming_po_units != null ? "open POs + net transfers" : "in transit")}
${kpi("Forecast", n0(forecastTotal), `units over ${forecast.length} ${unit}${forecast.length === 1 ? "" : "s"}${upliftTotal ? ` (incl. +${n0(upliftTotal)} event uplift)` : ""}`)}
${kpi("Recommended Order", n0(metrics.recommended_order_qty), `reorder by ${fmtDate(metrics.reorder_by_date)}`)}
${kpi("Reorder Point", n0(metrics.reorder_point), `${n0(metrics.lead_time_days)} days lead time, ${n0(metrics.safety_stock_used)} safety stock`)}
${model && isNum(model.tested_error?.wape) ? kpi("Tested Error", `${n1(model.tested_error.wape)}%`, model.model_label || model.model || "") : ""}
</div>
${insight ? `<div class="chart-card"><h3>DropSkip Insight</h3><div class="rich">${insight}</div></div>` : ""}
${statusHtml}
${fill.points.length ? `<div class="chart-card"><h3>${esc(fill.title)}</h3><div class="insight" style="margin:0"><ul class="pts">${fill.points.map((t) => `<li>${esc(t)}</li>`).join("")}</ul></div></div>` : ""}
<div class="chart-card"><h3>Sales history &amp; forecast</h3><div class="chart-wrap"><canvas id="c1"></canvas></div></div>
<div class="two-col">
<div class="chart-card"><h3>Forecast by ${esc(unit)}</h3>${table(
    [unit[0].toUpperCase() + unit.slice(1), "Forecast", ...(hasRange ? ["Likely range"] : []), ...(upliftTotal ? ["Event uplift"] : [])],
    forecastRows,
  )}</div>
<div class="chart-card"><h3>Reorder</h3>${table(["", ""], reorderRows)}</div>
</div>
${ledgerHtml(run?.ledger)}
${allocationHtml}
${methodHtml}
</div>
<script>
const D=${safeJson(chartData)};
Chart.defaults.color=D.theme.text;Chart.defaults.borderColor=D.theme.grid;
const ds=[{label:'Sales',data:D.history,borderColor:'#64748B',backgroundColor:'#64748B',tension:.25,spanGaps:false},{label:'Forecast',data:D.forecast,borderColor:'#2563EB',backgroundColor:'#2563EB',borderWidth:2.5,tension:.25,spanGaps:false}];
if(D.estimated){ds.unshift({label:'Estimated (sales fill, not real sales)',data:D.estimated,borderColor:'#94A3B8',backgroundColor:'#94A3B8',borderDash:[5,4],tension:.25,spanGaps:false});}
if(D.low){ds.push({label:'Likely range (low)',data:D.low,borderColor:'rgba(37,99,235,.35)',borderDash:[4,4],pointRadius:0,fill:false});ds.push({label:'Likely range (high)',data:D.high,borderColor:'rgba(37,99,235,.35)',backgroundColor:'rgba(37,99,235,.08)',borderDash:[4,4],pointRadius:0,fill:'-1'});}
new Chart(document.getElementById('c1'),{type:'line',data:{labels:D.labels,datasets:ds},options:{responsive:true,maintainAspectRatio:false,plugins:{tooltip:{mode:'index',intersect:false}},scales:{y:{beginAtZero:true,title:{display:true,text:'Units'}}}}});
</script></body></html>`;
};
